// client/src/components/pages/OperationOverview/index.tsx (converted from .js, ST-00216)
// Tabs: Route Detail (RouteDetail.tsx: cards or the sortable table, ST-00217),
// Executive Summary and Route Performance. The controls bar (search, period,
// alerts) and the header's Refresh / Export belong to Route Detail.
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import usePollingJs from '../../../hooks/usePolling';
import { FaCog, FaTruck, FaWarehouse, FaSyncAlt, FaFilePdf } from 'react-icons/fa';
import {
  HiOutlineCalendar,
  HiOutlineViewGrid,
  HiOutlineChartBar,
  HiOutlineSearch,
} from 'react-icons/hi';
import api from '../../../services/api';
import { formatMiningDate, formatMiningDateTime, toApiDateString } from '../../../utils/dateUtils';
import LexiBridgeLoaderJs from '../../common/LexiBridgeLoader';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import { PageHeader, SegmentedControl } from '../../../types/commonComponents';
import AlertBannerJs from './AlertBanner';
import ExecutiveSummary from './ExecutiveSummary';
import RoutePerformance from './RoutePerformance';
import RouteDetail from './RouteDetail';
import exportOverviewPDF from './exportOverviewPDF';
import { computeOverviewKpis, fallbackNoteOf, filterRouteCards } from './overviewKpis';
import { nextSort, sortGroupings, UNSORTED, type RouteSortKey } from './routeSort';
import type {
  DataFreshnessResponse,
  DeduplicatedTotals,
  GroupingsWithTotalsResponse,
  OverviewAlert,
  OverviewGrouping,
} from '../../../types/clientPages/operationOverview';

// Typed views of the JS modules.
const usePolling = usePollingJs as unknown as (
  callback: () => void,
  intervalMs: number,
  options?: { enabled?: boolean; immediate?: boolean }
) => void;
const LexiBridgeLoader = LexiBridgeLoaderJs as unknown as ComponentType<{
  message?: string;
  compact?: boolean;
}>;
const AlertBanner = AlertBannerJs as unknown as ComponentType<{ alerts?: OverviewAlert[] }>;

type OverviewTab = 'overview' | 'executive' | 'performance';

const OperationOverview = () => {
  const navigate = useNavigate();
  const [groupings, setGroupings] = useState<OverviewGrouping[]>([]);
  const [deduplicatedTotals, setDeduplicatedTotals] = useState<DeduplicatedTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // The Route Detail table's sort; kept here so the PDF export follows it.
  const [sortConfig, setSortConfig] = useState(UNSORTED);

  // Date range state - ensuring full day coverage for location analytics
  // On the 1st of the month, default to the full previous month range
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth() - 1, 1);
    }
    const date = new Date();
    date.setDate(1); // First day of current month
    return date;
  });

  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
    }
    const date = new Date();
    date.setDate(date.getDate() - 1); // Yesterday
    return date;
  });

  // Active date range state (what's currently displayed)
  const [activeStartDate, setActiveStartDate] = useState(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth() - 1, 1);
    }
    const date = new Date();
    date.setDate(1);
    return date;
  });

  const [activeEndDate, setActiveEndDate] = useState(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
    }
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  });

  // Track if dates have been changed but not yet applied
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // Alert banner state
  const [alerts, setAlerts] = useState<OverviewAlert[]>([]);

  // Tab state
  const [activeTab, setActiveTab] = useState<OverviewTab>('overview');

  // Performance optimization state
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Data freshness tracking
  const [hasNewerData, setHasNewerData] = useState(false);
  const [dataLoadedAt, setDataLoadedAt] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchGroupings = useCallback(
    async (forceRefresh = false) => {
      if (!activeStartDate || !activeEndDate) return;

      // Cancel previous request if it exists
      if (abortController) {
        abortController.abort();
      }

      // Create new abort controller
      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      try {
        if (forceRefresh) {
          setIsRefreshing(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const startDateStr = toApiDateString(activeStartDate);
        const endDateStr = toApiDateString(activeEndDate);
        console.log(
          'Fetching groupings with totals for date range:',
          startDateStr,
          'to',
          endDateStr,
          forceRefresh ? '(force refresh)' : ''
        );
        const response = await api.routeSummary.getGroupingsWithTotals(startDateStr, endDateStr, {
          signal: newAbortController.signal,
          forceRefresh,
        });

        const data = response.data as GroupingsWithTotalsResponse;
        if (data && !Array.isArray(data) && data.groupings) {
          console.log(
            'Received groupings data:',
            data.groupings.length,
            'groupings (with dedup totals)'
          );
          setGroupings(data.groupings);
          setDeduplicatedTotals(data.deduplicatedTotals || null);
        } else {
          const groupingsArr = Array.isArray(data) ? data : [];
          console.log('Received groupings data:', groupingsArr.length, 'groupings');
          setGroupings(groupingsArr);
          setDeduplicatedTotals(null);
        }
        setDataLoadedAt(new Date());
        setHasNewerData(false);
        setLoading(false);
        setIsRefreshing(false);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError('Failed to load operation overview data');
          setLoading(false);
          setIsRefreshing(false);
          console.error('Error fetching groupings:', err);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeStartDate, activeEndDate]
  );

  useEffect(() => {
    console.log('[DEBUG] OperationOverview MOUNT - fetchGroupings effect');
    fetchGroupings();

    // Cleanup function
    return () => {
      console.log('[DEBUG] OperationOverview UNMOUNT - cleanup');
      if (abortController) {
        abortController.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStartDate, activeEndDate]);

  // Poll for data freshness every 30 seconds — paused while this tab is in the
  // background, with one catch-up check when it comes back.
  const checkFreshness = useCallback(async () => {
    if (!activeStartDate || !activeEndDate || !dataLoadedAt) return;
    try {
      const startDateStr = toApiDateString(activeStartDate);
      const endDateStr = toApiDateString(activeEndDate);
      const response = await api.routeSummary.getDataFreshness(startDateStr, endDateStr);
      if ((response.data as DataFreshnessResponse).hasNewerData) {
        setHasNewerData(true);
      }
    } catch {
      // Silently ignore freshness check errors
    }
  }, [activeStartDate, activeEndDate, dataLoadedAt]);

  usePolling(checkFreshness, 30000);

  // Fetch alerts on mount
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await api.routeSummary.getOverviewAlerts();
        setAlerts((response.data as OverviewAlert[] | null) || []);
      } catch {
        // Silently ignore - alerts are non-critical
      }
    };
    fetchAlerts();
  }, []);

  // Handle refresh button click
  const handleRefreshData = () => {
    fetchGroupings(true);
  };

  // Handle date changes from the picker
  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    setHasPendingChanges(true);
  };

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
    setHasPendingChanges(true);
  };

  // Apply date changes and fetch new data
  const handleApplyDateFilter = () => {
    setActiveStartDate(startDate);
    setActiveEndDate(endDate);
    setHasPendingChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gray-50">
        <LexiBridgeLoader message="Loading operations" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md shadow-md"
        role="alert"
      >
        <div className="flex">
          <div className="py-1">
            <svg
              className="h-6 w-6 text-red-500 mr-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <p className="font-bold">Error!</p>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // A click on a table heading: ascending first, descending on a second click.
  const handleSort = (key: RouteSortKey) => setSortConfig((current) => nextSort(current, key));

  // Navigate to Route Summary with pre-selected grouping
  const handleViewDetails = (grouping: OverviewGrouping) => {
    const params = new URLSearchParams({
      groupingId: grouping.id.toString(),
      startDate: toApiDateString(activeStartDate),
      endDate: toApiDateString(activeEndDate),
    });
    navigate(`/route-summary?${params.toString()}`, {
      state: { fromOperationsOverview: true, groupingName: grouping.name },
    });
  };

  // The cards on screen, and the headline tiles that must agree with them
  // (ST-00215: overviewKpis.ts). With no search the headline is the server's
  // overlap-safe total for exactly these cards; a search sums the cards shown.
  const filteredGroupings = filterRouteCards(groupings, searchTerm);
  const summaryStats = computeOverviewKpis(filteredGroupings, deduplicatedTotals);
  // ST-00218 P3: one note when a "Truck + time" route shows delivery-number figures.
  const matchingNote = fallbackNoteOf(filteredGroupings, deduplicatedTotals);

  const handleExportPDF = () => {
    exportOverviewPDF({
      groupings: sortGroupings(filteredGroupings, sortConfig),
      summaryStats,
      startDate: activeStartDate,
      endDate: activeEndDate,
      matchingNote,
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={HiOutlineViewGrid}
        title="Operations Overview"
        inline={false}
        toolbarAlign="start"
        toolbar={
          <SegmentedControl
            ariaLabel="Operations Overview view"
            value={activeTab}
            onChange={setActiveTab}
            className="page-header__segments-wrap"
            options={[
              { value: 'overview', label: 'Route Detail' },
              { value: 'executive', label: 'Executive Summary' },
              { value: 'performance', label: 'Route Performance' },
            ]}
          />
        }
        actions={
          activeTab === 'overview' && (
            <>
              {/* Data Freshness Button */}
              <button
                type="button"
                onClick={handleRefreshData}
                disabled={isRefreshing || loading}
                aria-label={isRefreshing ? 'Refreshing...' : hasNewerData ? 'New data' : 'Refresh'}
                className={`relative inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 text-sm font-medium rounded-lg transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  hasNewerData
                    ? 'bg-amber-50 text-amber-700 border-2 border-amber-400 shadow-sm hover:bg-amber-100'
                    : 'border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50'
                } ${isRefreshing ? 'opacity-75 cursor-wait' : ''}`}
                title={
                  hasNewerData
                    ? 'New data available - click to refresh'
                    : dataLoadedAt
                      ? `Data loaded ${formatMiningDateTime(dataLoadedAt)}`
                      : 'Refresh data'
                }
              >
                {hasNewerData && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                  </span>
                )}
                <FaSyncAlt
                  className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                <span className="page-header__label">
                  {isRefreshing ? 'Refreshing...' : hasNewerData ? 'New data' : 'Refresh'}
                </span>
              </button>

              {/* Export PDF */}
              {filteredGroupings.length > 0 && (
                <button
                  type="button"
                  onClick={handleExportPDF}
                  aria-label="Export as PDF"
                  title="Export as PDF"
                  className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <FaFilePdf className="w-3.5 h-3.5 text-slate-500" aria-hidden="true" />
                  <span className="page-header__label">Export</span>
                </button>
              )}
            </>
          )
        }
      />

      {/* Executive Summary Tab */}
      {activeTab === 'executive' && (
        <ExecutiveSummary
          startDate={activeStartDate}
          endDate={activeEndDate}
          alerts={alerts}
          groupings={groupings}
        />
      )}

      {/* Route Performance Tab */}
      {activeTab === 'performance' && (
        <RoutePerformance startDate={activeStartDate} endDate={activeEndDate} />
      )}

      {/* Route Detail Tab */}
      {activeTab === 'overview' && (
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Controls Bar - Card-based design */}
          <div
            className={`bg-surface rounded-xl border-2 shadow-sm overflow-hidden mb-4 sm:mb-6 transition-all duration-200 ${
              hasPendingChanges ? 'border-amber-400' : 'border-slate-200'
            }`}
          >
            <div className="p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
              {/* Left: Search and Date */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 flex-1">
                {/* Search */}
                <div className="relative flex-1 sm:max-w-md">
                  <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search routes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-surface"
                  />
                </div>

                {/* Date Range */}
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 text-slate-600 flex-shrink-0">
                    <HiOutlineCalendar className="w-5 h-5" />
                    <span className="text-xs font-semibold uppercase tracking-wider hidden sm:inline">
                      Period
                    </span>
                  </div>
                  <div className="flex-1 sm:flex-none sm:w-72">
                    <UnifiedDateRangePicker
                      startDate={startDate}
                      onStartDateChange={handleStartDateChange}
                      endDate={endDate}
                      onEndDateChange={handleEndDateChange}
                      hasPendingChanges={hasPendingChanges}
                    />
                  </div>
                </div>
              </div>

              {/* Right: Update Button or Active Period */}
              {hasPendingChanges ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center text-amber-600 text-sm">
                    <svg
                      className="w-4 h-4 mr-1.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-medium hidden sm:inline">Dates changed</span>
                  </div>
                  <button
                    onClick={handleApplyDateFilter}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-sm font-medium text-sm whitespace-nowrap"
                  >
                    Update Data
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                  <div className="text-xs text-slate-500">
                    <span className="hidden sm:inline">Showing </span>
                    <span className="font-medium text-slate-700">
                      {formatMiningDate(activeStartDate)}
                    </span>
                    <span className="mx-1">→</span>
                    <span className="font-medium text-slate-700">
                      {formatMiningDate(activeEndDate)}
                    </span>
                  </div>
                  {dataLoadedAt && (
                    <div
                      className={`text-[11px] px-2 py-0.5 rounded-full ${
                        hasNewerData
                          ? 'bg-amber-50 text-amber-600 border border-amber-200'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {hasNewerData ? 'Stale' : 'Loaded'}{' '}
                      {dataLoadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Alert Banner */}
          <AlertBanner alerts={alerts} />

          {/* Empty State */}
          {filteredGroupings.length === 0 && (
            <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 px-4 sm:px-8 py-8 sm:py-10 text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 left-10 w-20 h-20 border-2 border-white rounded-full"></div>
                  <div className="absolute bottom-4 right-10 w-32 h-32 border-2 border-white rounded-full"></div>
                </div>
                <div className="relative w-20 h-20 bg-surface/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FaCog className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">No Route Groupings Found</h3>
                <p className="text-primary-100 text-sm max-w-md mx-auto">
                  Create route groupings in Settings → Account Setup to organize and track your
                  transportation routes effectively.
                </p>
              </div>
              <div className="p-6 bg-slate-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-surface rounded-xl p-4 border border-slate-200 text-center">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <FaTruck className="w-5 h-5 text-orange-600" />
                    </div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Track Dispatches</h4>
                    <p className="text-xs text-slate-500">
                      Monitor outgoing shipments from multiple locations
                    </p>
                  </div>
                  <div className="bg-surface rounded-xl p-4 border border-slate-200 text-center">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <FaWarehouse className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Track Receipts</h4>
                    <p className="text-xs text-slate-500">
                      Monitor incoming shipments at destination points
                    </p>
                  </div>
                  <div className="bg-surface rounded-xl p-4 border border-slate-200 text-center">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <HiOutlineChartBar className="w-5 h-5 text-primary-600" />
                    </div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Analyze Variance</h4>
                    <p className="text-xs text-slate-500">
                      Identify discrepancies between dispatched and received
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Route Detail: KPI strip, then the cards or the sortable table */}
          {filteredGroupings.length > 0 && (
            <RouteDetail
              groupings={filteredGroupings}
              summaryStats={summaryStats}
              searchTerm={searchTerm}
              sortConfig={sortConfig}
              onSort={handleSort}
              onViewDetails={handleViewDetails}
              matchingNote={matchingNote}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default OperationOverview;
